import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    AlertTriangle,
    CalendarPlus,
    Check,
    ChevronDown,
    ChevronUp,
    HeartHandshake,
    MapPin,
    Minus,
    Navigation,
    RefreshCw,
    Search,
    UserPlus,
    Zap,
} from 'lucide-react';
import { supabase } from '../../../src/lib/supabase';
import { useAuth } from '../../../src/contexts/AuthContext';
import { useToast } from '../../../src/contexts/ToastContext';
import { Profile, PosVendaSugestao } from '../../../types';
import LoadingSpinner from '../../../src/components/LoadingSpinner';
import ResponsibleFilter from '../../../src/components/ResponsibleFilter';
import CriarVisitaModal from '../../../src/components/CriarVisitaModal';
import { META_PLANEJADAS_DIA } from '../Rotas/rotaTypes';
import { haversineKm } from '../../../src/lib/routeOptimizer';

/** Faixas do filtro "dias mínimos sem contato" (spec §5). */
const FAIXAS_DIAS = [0, 30, 45, 60, 90];

/**
 * Teto da atribuição em lote. É o mesmo `META_PLANEJADAS_DIA` das Rotas de propósito:
 * o lote inteiro cai num único dia, e um dia comporta 15 visitas planejadas (ROT-R07).
 * Deixar marcar 40 clientes seria oferecer um dia impossível de cumprir.
 */
const LIMITE_LOTE = META_PLANEJADAS_DIA;

/**
 * Volume mínimo para o cliente aparecer na tela. Abaixo disso a visita não paga o
 * deslocamento. É ocultação de tela: a view `vw_pos_venda_sugestoes` continua
 * devolvendo todo mundo, então quem consultar o banco direto vê a base inteira.
 */
const CONSUMO_MINIMO = 700;

/**
 * Checkbox da tabela. Nativo estilizado não fica consistente entre navegadores nem
 * no dark mode — aqui o input real fica escondido (`sr-only`, preservando teclado e
 * leitor de tela) e o quadrado é desenhado.
 */
const SelectBox: React.FC<{
    checked: boolean;
    onChange: () => void;
    disabled?: boolean;
    /** Header com seleção parcial: mostra traço em vez de check. */
    parcial?: boolean;
    label: string;
}> = ({ checked, onChange, disabled, parcial, label }) => (
    <label
        className={`inline-flex items-center justify-center ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
        title={label}
    >
        <input
            type="checkbox"
            checked={checked}
            disabled={disabled}
            onChange={onChange}
            aria-label={label}
            className="sr-only peer"
        />
        <span
            className={`w-[18px] h-[18px] rounded-md border-2 flex items-center justify-center transition-all
                peer-focus-visible:ring-2 peer-focus-visible:ring-primary/40 peer-focus-visible:ring-offset-1
                dark:peer-focus-visible:ring-offset-gray-800
                ${checked || parcial
                    ? 'bg-primary border-primary text-white'
                    : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500'}
                ${disabled ? 'opacity-30' : (!checked && !parcial ? 'hover:border-primary' : 'hover:opacity-90')}`}
        >
            {parcial && !checked
                ? <Minus size={12} strokeWidth={3.5} />
                : checked ? <Check size={12} strokeWidth={3.5} /> : null}
        </span>
    </label>
);

/**
 * Mesma lista do seletor de responsável das Rotas (`ROLES_CAMPO` em
 * views/CRM/Rotas/RotasView.tsx): só quem executa visita em campo. Sem isso o
 * seletor de vendedor enche de Financeiro, Marketing e Tecnologia.
 */
const ROLES_CAMPO: string[] = ['adm', 'gerente', 'vendedor', 'motoboy', 'vendedor_parceiro', 'parceiro'];

const nomeDe = (profiles: Profile[], id?: string | null) =>
    (id && profiles.find(p => p.id === id)?.full_name) || 'Sem responsável';

/** Colunas ordenáveis. Checkbox e Ação ficam de fora — não têm dado para ordenar. */
type SortKey = 'cliente_nome' | 'cidade' | 'vendedor' | 'dias_sem_contato' | 'consumo_medio_kwh';

/** Ponto utilizável para distância. `null` quando a negociação não foi geocodificada. */
const pontoDe = (l: PosVendaSugestao) =>
    l.latitude != null && l.longitude != null ? { lat: l.latitude, lng: l.longitude } : null;

/**
 * Só 'endereco' é coordenada do cliente. Todo o resto é aproximação de raio
 * conhecido, e a tela precisa dizer isso: um pino de nível 'cidade' fica no centro
 * do município (mais jitter de ~600 m), o que serve para saber que o cliente está
 * em Anápolis e não para dizer que ele está a 1,2 km de outro cliente de Anápolis.
 *
 * 'pendente' entra aqui de propósito: quem tem endereço de UC alterado volta para
 * a fila mantendo a coordenada velha até o cron reprocessar (ver geocode-leads), e
 * nesse intervalo a coordenada existe mas já se sabe desatualizada.
 */
const geoAproximado = (l: PosVendaSugestao) => l.geocode_status !== 'endereco';

/** Texto do aviso, por nível — o vendedor precisa saber QUANTO o pino erra. */
const PRECISAO_APROXIMADA: Record<string, string> = {
    bairro: 'localização no nível do bairro, não do endereço',
    cidade: 'localização só no nível da cidade — o pino fica no centro do município',
    pendente: 'endereço mudou e a localização ainda não foi refeita',
    falhou: 'não foi possível localizar o endereço',
};
const avisoPrecisao = (l: PosVendaSugestao) =>
    PRECISAO_APROXIMADA[l.geocode_status || ''] || 'localização aproximada';

const COLUNAS: Array<{ key: SortKey; label: string; numerica?: boolean }> = [
    { key: 'cliente_nome', label: 'Cliente' },
    { key: 'cidade', label: 'Cidade' },
    { key: 'vendedor', label: 'Vendedor responsável' },
    { key: 'dias_sem_contato', label: 'Dias sem contato', numerica: true },
    { key: 'consumo_medio_kwh', label: 'Volume (kWh)', numerica: true },
];

const corDias = (d: number) =>
    d >= 90 ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
        : d >= 60 ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
            : d >= 30 ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';

/**
 * Central de Sugestões de Pós-venda (visão do gestor) — rota `/crm/pos-venda`.
 *
 * Lista clientes ativos e adimplentes ordenados por tempo sem contato e permite
 * atribuir a visita a um vendedor num dia da grade. A elegibilidade toda mora na
 * view `vw_pos_venda_sugestoes` (migration 20260804120000) — aqui não se recalcula
 * regra nenhuma, só se filtra e se atribui.
 *
 * "Adimplente" vem derivado de faturas (boleto Vigor vencido), não do campo manual
 * unidades_consumidoras.status_financeiro — que nasce 'Não se aplica' e ninguém preenche.
 */
const PosVendaView: React.FC = () => {
    const { profile } = useAuth();
    const { addToast } = useToast();

    const [profiles, setProfiles] = useState<Profile[]>([]);
    const [linhas, setLinhas] = useState<PosVendaSugestao[]>([]);
    const [loading, setLoading] = useState(true);
    const [erro, setErro] = useState<string | null>(null);

    const [minDias, setMinDias] = useState(30);
    const [vendedoresSel, setVendedoresSel] = useState<string[]>([]);
    const [cidade, setCidade] = useState('');
    const [busca, setBusca] = useState('');
    // Cliente inadimplente some das sugestões (spec §4), mas o gestor pode querer ver
    // a lista inteira — inclusive para entender por que alguém sumiu.
    const [incluirInadimplentes, setIncluirInadimplentes] = useState(false);

    // Atribuição: uma linha (botão da linha) ou várias de uma vez (checkboxes).
    // Nos dois casos abre o MESMO modal — ele aceita lista.
    const [atribuir, setAtribuir] = useState<PosVendaSugestao[] | null>(null);
    const [selecionados, setSelecionados] = useState<number[]>([]);

    // Ordenação por coluna, mesmo padrão de views/Financeiro/UnidadesList.tsx.
    // Default = a ordem que a view devolve (mais tempo sem contato primeiro), que é
    // a do motor de sugestão e o que o gestor espera ao abrir a tela.
    const [sortConfig, setSortConfig] = useState<{ key: SortKey; direction: 'asc' | 'desc' }>(
        { key: 'dias_sem_contato', direction: 'desc' }
    );
    const handleSort = (key: SortKey) => {
        setAncora(null); // ordenar por coluna sai do modo proximidade — são duas ordens concorrentes
        setSortConfig(c => ({
            key,
            direction: c.key === key && c.direction === 'asc' ? 'desc' : 'asc',
        }));
    };

    // Modo proximidade: a linha clicada sobe para o topo e o resto se reordena por
    // distância até ela, então os vizinhos imediatos passam a ser os mais próximos.
    const [ancora, setAncora] = useState<number | null>(null);

    const carregar = useCallback(async () => {
        setErro(null);
        const { data, error } = await supabase
            .from('vw_pos_venda_sugestoes')
            .select('*')
            .order('dias_sem_contato', { ascending: false })
            .limit(500);
        if (error) {
            console.error('Erro ao carregar sugestões de pós-venda:', error);
            setErro(error.message);
            setLinhas([]);
        } else {
            setLinhas((data || []) as PosVendaSugestao[]);
        }
    }, []);

    useEffect(() => {
        const init = async () => {
            setLoading(true);
            const [{ data: profs }] = await Promise.all([
                supabase.from('profiles').select('*').neq('role', 'consumidor'),
                carregar(),
            ]);
            if (profs) {
                setProfiles(
                    (profs as Profile[]).filter(
                        p => (p as any).status !== 'inactive' && ROLES_CAMPO.includes((p as any).role)
                    )
                );
            }
            setLoading(false);
        };
        init();
    }, [carregar]);

    const cidades = useMemo(
        () => Array.from(new Set(linhas.map(l => l.cidade).filter(Boolean) as string[])).sort(),
        [linhas]
    );

    const filtradas = useMemo(() => linhas.filter(l => {
        // Consumo em branco também some: sem volume conhecido não dá para justificar
        // o deslocamento, e deixar passar encheria a lista de cadastro incompleto.
        if ((Number(l.consumo_medio_kwh) || 0) < CONSUMO_MINIMO) return false;
        if (!incluirInadimplentes && !l.adimplente) return false;
        if (l.dias_sem_contato < minDias) return false;
        if (vendedoresSel.length && !vendedoresSel.includes(l.responsavel_id || '')) return false;
        if (cidade && l.cidade !== cidade) return false;
        if (busca && !l.cliente_nome?.toLowerCase().includes(busca.toLowerCase())) return false;
        return true;
    }), [linhas, incluirInadimplentes, minDias, vendedoresSel, cidade, busca]);

    /**
     * Lista efetivamente exibida. TUDO downstream (seleção, "marcar todos", total de
     * kWh) usa esta, não `filtradas`: "os 15 primeiros" precisa querer dizer os 15
     * primeiros que o gestor está vendo, não os da ordem em que a view devolveu.
     */
    const visiveis = useMemo(() => {
        // Modo proximidade vence a ordenação por coluna enquanto estiver ativo.
        const linhaAncora = ancora != null ? filtradas.find(l => l.negociacao_id === ancora) : undefined;
        const pontoAncora = linhaAncora ? pontoDe(linhaAncora) : null;
        if (linhaAncora && pontoAncora) {
            const outros = filtradas.filter(l => l.negociacao_id !== ancora);
            const comDist = outros.map(l => {
                const p = pontoDe(l);
                // Sem coordenada vai para o fim — mesmo tratamento da Prospecção Geo.
                return { l, km: p ? haversineKm(pontoAncora, p) : Infinity };
            });
            comDist.sort((a, b) => a.km - b.km);
            return [linhaAncora, ...comDist.map(x => x.l)];
        }

        const { key, direction } = sortConfig;
        const sinal = direction === 'asc' ? 1 : -1;
        // Vendedor ordena pelo NOME exibido — ordenar por responsavel_id agruparia por
        // uuid, que na tela parece aleatório.
        const valor = (l: PosVendaSugestao): string | number | null =>
            key === 'vendedor' ? nomeDe(profiles, l.responsavel_id) : (l[key] ?? null);

        return [...filtradas].sort((a, b) => {
            const va = valor(a);
            const vb = valor(b);
            // Vazio sempre no fim, nas duas direções — cidade em branco não disputa o topo.
            if (va === null || va === '') return vb === null || vb === '' ? 0 : 1;
            if (vb === null || vb === '') return -1;
            // localeCompare pt-BR: sem ele "Ávila" cairia depois de "Zanetti".
            const cmp = typeof va === 'number' && typeof vb === 'number'
                ? va - vb
                : String(va).localeCompare(String(vb), 'pt-BR', { sensitivity: 'base' });
            return cmp * sinal;
        });
    }, [filtradas, sortConfig, profiles, ancora]);

    /** Distância até a âncora, por linha — usada só para exibir na coluna Cidade. */
    const distanciaKm = useMemo(() => {
        const linhaAncora = ancora != null ? filtradas.find(l => l.negociacao_id === ancora) : undefined;
        const pontoAncora = linhaAncora ? pontoDe(linhaAncora) : null;
        if (!pontoAncora) return new Map<number, number>();
        const m = new Map<number, number>();
        for (const l of filtradas) {
            const p = pontoDe(l);
            if (p && l.negociacao_id !== ancora) m.set(l.negociacao_id, haversineKm(pontoAncora, p));
        }
        return m;
    }, [filtradas, ancora]);

    const linhaAncora = ancora != null ? filtradas.find(l => l.negociacao_id === ancora) : undefined;

    /** Clique na linha liga/desliga o modo proximidade a partir dela. */
    const clicarLinha = (l: PosVendaSugestao) => {
        if (ancora === l.negociacao_id) { setAncora(null); return; }
        if (!pontoDe(l)) {
            addToast(`${l.cliente_nome} não tem localização — não dá para medir distância a partir dele.`, 'info');
            return;
        }
        setAncora(l.negociacao_id);
    };

    const kwhTotal = useMemo(
        () => visiveis.reduce((s, l) => s + (Number(l.consumo_medio_kwh) || 0), 0),
        [visiveis]
    );

    const opcoesResponsavel = useMemo(
        () => profiles.map(p => ({ id: p.id, full_name: p.full_name, email: p.email })),
        [profiles]
    );

    // A seleção é mantida por id e sobrevive a mudanças de filtro; o que a ação usa
    // são as linhas ainda visíveis, para o gestor nunca atribuir algo que saiu da tela.
    const selecionadasVisiveis = useMemo(
        () => visiveis.filter(l => selecionados.includes(l.negociacao_id)),
        [visiveis, selecionados]
    );
    const noLimite = selecionados.length >= LIMITE_LOTE;
    // "Marcar todos" marca no máximo o que cabe no lote.
    const alvoTodas = Math.min(visiveis.length, LIMITE_LOTE);
    const todasSelecionadas = alvoTodas > 0 && selecionadasVisiveis.length >= alvoTodas;
    const parcialSelecionadas = selecionadasVisiveis.length > 0 && !todasSelecionadas;

    const toggleLinha = (id: number) => {
        setSelecionados(prev => {
            if (prev.includes(id)) return prev.filter(x => x !== id);
            if (prev.length >= LIMITE_LOTE) {
                addToast(`Máximo de ${LIMITE_LOTE} clientes por atribuição — é o que cabe num dia de rota.`, 'info');
                return prev;
            }
            return [...prev, id];
        });
    };

    const toggleTodas = () => {
        if (todasSelecionadas) {
            setSelecionados(prev => prev.filter(id => !visiveis.some(l => l.negociacao_id === id)));
            return;
        }
        setSelecionados(prev => {
            const vaga = LIMITE_LOTE - prev.length;
            // Fatia na ordem EXIBIDA — se o gestor ordenou por kWh, marca os maiores.
            const novos = visiveis
                .map(l => l.negociacao_id)
                .filter(id => !prev.includes(id))
                .slice(0, Math.max(0, vaga));
            if (novos.length < visiveis.length - selecionadasVisiveis.length) {
                addToast(`Marcados os ${LIMITE_LOTE} primeiros — é o limite de um lote.`, 'info');
            }
            return [...prev, ...novos];
        });
    };

    /** Converte a linha da view no recorte que o modal de criação de visita espera. */
    const paraLead = (l: PosVendaSugestao) => ({
        id: l.negociacao_id,
        titulo: l.cliente_nome,
        cliente_nome: l.cliente_nome,
        responsavel_id: l.responsavel_id,
        motivoSugestao: `${l.dias_sem_contato} dias sem contato`,
    });

    if (loading) return <LoadingSpinner />;

    return (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-4 sm:p-5 space-y-4">
            {/* Cabeçalho + KPIs */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div className="flex items-center gap-2.5">
                    <div className="p-2 rounded-xl bg-secondary/10 text-secondary">
                        <HeartHandshake size={18} />
                    </div>
                    <div>
                        <h3 className="text-sm font-bold text-gray-900 dark:text-white">Central de Sugestões — Pós-venda</h3>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400">
                            Clientes com 700 kWh ou mais, sem contato há mais tempo. Clique numa linha para ver os vizinhos mais próximos.
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <div className="px-3 py-1.5 rounded-xl bg-gray-50 dark:bg-gray-700/50 text-center">
                        <p className="text-sm font-extrabold text-gray-900 dark:text-white">{visiveis.length}</p>
                        <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wider">Clientes</p>
                    </div>
                    <div className="px-3 py-1.5 rounded-xl bg-gray-50 dark:bg-gray-700/50 text-center">
                        <p className="text-sm font-extrabold text-gray-900 dark:text-white">{kwhTotal.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}</p>
                        <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wider">kWh</p>
                    </div>
                    <button
                        onClick={carregar}
                        className="p-2 rounded-xl text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        title="Recarregar sugestões"
                    >
                        <RefreshCw size={16} />
                    </button>
                </div>
            </div>

            {/* Filtros */}
            <div className="flex flex-wrap items-center gap-2">
                <div className="flex items-center gap-1 p-1 rounded-full bg-gray-100 dark:bg-gray-700">
                    {FAIXAS_DIAS.map(d => (
                        <button
                            key={d}
                            onClick={() => setMinDias(d)}
                            className={`px-3 py-1 text-xs font-bold rounded-full transition-colors ${
                                minDias === d
                                    ? 'bg-white dark:bg-gray-800 text-primary shadow-sm'
                                    : 'text-gray-500 dark:text-gray-300 hover:text-gray-700'
                            }`}
                        >
                            {d === 0 ? 'Todos' : `${d}+ dias`}
                        </button>
                    ))}
                </div>

                <ResponsibleFilter
                    selectedResponsibles={vendedoresSel}
                    setSelectedResponsibles={setVendedoresSel}
                    profiles={profiles}
                    currentUserProfile={profile}
                    label="Vendedor"
                    allLabel="Todos os vendedores"
                    unrestricted
                    className="md:w-auto"
                />

                <select
                    value={cidade}
                    onChange={e => setCidade(e.target.value)}
                    className="px-3 py-1.5 text-sm rounded-full bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/30"
                >
                    <option value="">Todas as cidades</option>
                    {cidades.map(c => <option key={c} value={c}>{c}</option>)}
                </select>

                <div className="relative">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                        value={busca}
                        onChange={e => setBusca(e.target.value)}
                        placeholder="Buscar cliente"
                        className="pl-8 pr-3 py-1.5 text-sm rounded-full bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/30"
                    />
                </div>

                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600">
                    <SelectBox
                        checked={incluirInadimplentes}
                        onChange={() => setIncluirInadimplentes(v => !v)}
                        label="Mostrar inadimplentes"
                    />
                    <button
                        onClick={() => setIncluirInadimplentes(v => !v)}
                        className="text-xs font-medium text-gray-600 dark:text-gray-300"
                    >
                        Mostrar inadimplentes
                    </button>
                </div>
            </div>

            {erro && (
                <div className="flex items-start gap-2 px-4 py-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                    <AlertTriangle size={16} className="text-red-500 shrink-0 mt-0.5" />
                    <p className="text-xs text-red-700 dark:text-red-300">
                        Não foi possível carregar as sugestões: {erro}
                    </p>
                </div>
            )}

            {/* Modo proximidade ativo */}
            {linhaAncora && (
                <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 rounded-xl bg-secondary/10 border border-secondary/30">
                    <p className="text-xs font-bold text-gray-700 dark:text-gray-200 flex items-center gap-1.5">
                        <Navigation size={13} className="text-secondary" />
                        Ordenado por proximidade de <span className="text-secondary">{linhaAncora.cliente_nome}</span>
                        {geoAproximado(linhaAncora) ? (
                            <span className="font-medium text-gray-500 dark:text-gray-400">
                                · {avisoPrecisao(linhaAncora)}, a ordem por distância não é confiável
                            </span>
                        ) : null}
                    </p>
                    <button
                        onClick={() => setAncora(null)}
                        className="px-3 py-1.5 text-xs font-semibold rounded-full text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                        Voltar à ordem normal
                    </button>
                </div>
            )}

            {/* Ação em lote — só aparece com algo selecionado */}
            {selecionadasVisiveis.length > 0 && (
                <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 rounded-xl bg-primary/5 border border-primary/30">
                    <p className="text-xs font-bold text-primary">
                        {selecionadasVisiveis.length} de {LIMITE_LOTE} {selecionadasVisiveis.length === 1 ? 'cliente' : 'clientes'}
                        <span className="font-medium text-gray-500 dark:text-gray-400">
                            {' · '}
                            {selecionadasVisiveis
                                .reduce((s, l) => s + (Number(l.consumo_medio_kwh) || 0), 0)
                                .toLocaleString('pt-BR', { maximumFractionDigits: 0 })} kWh
                            {noLimite && ' · limite do lote atingido'}
                        </span>
                    </p>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setSelecionados([])}
                            className="px-3 py-1.5 text-xs font-semibold rounded-full text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        >
                            Limpar seleção
                        </button>
                        <button
                            onClick={() => setAtribuir(selecionadasVisiveis)}
                            className="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-bold rounded-full bg-primary text-white hover:opacity-90 transition-opacity"
                        >
                            <CalendarPlus size={13} />
                            Atribuir {selecionadasVisiveis.length} {selecionadasVisiveis.length === 1 ? 'visita' : 'visitas'}
                        </button>
                    </div>
                </div>
            )}

            {/* Lista */}
            {visiveis.length === 0 ? (
                <div className="py-16 text-center">
                    <HeartHandshake size={32} className="mx-auto text-gray-300 dark:text-gray-600 mb-3" />
                    <p className="text-sm font-bold text-gray-600 dark:text-gray-300">Nenhum cliente sugerido</p>
                    <p className="text-xs text-gray-400 mt-1">
                        Ou todo mundo teve contato recente, ou já existe visita de pós-venda em aberto.
                    </p>
                </div>
            ) : (
                <div className="overflow-x-auto rounded-2xl border border-gray-100 dark:border-gray-700">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50 dark:bg-gray-700/50">
                            <tr className="text-left text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">
                                <th className="pl-4 pr-1 py-2.5 w-9">
                                    <SelectBox
                                        checked={todasSelecionadas}
                                        parcial={parcialSelecionadas}
                                        onChange={toggleTodas}
                                        label={todasSelecionadas
                                            ? 'Desmarcar todos'
                                            : `Marcar os ${alvoTodas} primeiros`}
                                    />
                                </th>
                                {COLUNAS.map(c => {
                                    const ativa = sortConfig.key === c.key;
                                    return (
                                        <th
                                            key={c.key}
                                            onClick={() => handleSort(c.key)}
                                            aria-sort={ativa ? (sortConfig.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
                                            className={`px-4 py-2.5 font-bold cursor-pointer select-none transition-colors hover:text-primary ${
                                                ativa ? 'text-primary' : ''
                                            } ${c.numerica ? 'text-right' : ''}`}
                                        >
                                            <div className={`flex items-center gap-1 ${c.numerica ? 'justify-end' : ''}`}>
                                                {c.label}
                                                {ativa && (sortConfig.direction === 'asc'
                                                    ? <ChevronUp size={13} />
                                                    : <ChevronDown size={13} />)}
                                            </div>
                                        </th>
                                    );
                                })}
                                <th className="px-4 py-2.5 font-bold text-right">Ação</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                            {visiveis.map(l => {
                                const marcada = selecionados.includes(l.negociacao_id);
                                const ehAncora = ancora === l.negociacao_id;
                                const km = distanciaKm.get(l.negociacao_id);
                                return (
                                <tr
                                    key={l.negociacao_id}
                                    onClick={() => clicarLinha(l)}
                                    title={ehAncora
                                        ? 'Clique para voltar à ordem normal'
                                        : 'Clique para ordenar por proximidade deste cliente'}
                                    className={`transition-colors cursor-pointer ${
                                        ehAncora ? 'bg-secondary/10 ring-1 ring-inset ring-secondary/40'
                                            : marcada ? 'bg-primary/5'
                                                : 'hover:bg-gray-50 dark:hover:bg-gray-700/30'}`}
                                >
                                    {/* O checkbox não pode disparar o clique da linha. */}
                                    <td className="pl-4 pr-1 py-2.5" onClick={e => e.stopPropagation()}>
                                        <SelectBox
                                            checked={marcada}
                                            disabled={!marcada && noLimite}
                                            onChange={() => toggleLinha(l.negociacao_id)}
                                            label={!marcada && noLimite
                                                ? `Limite de ${LIMITE_LOTE} por lote atingido`
                                                : l.cliente_nome}
                                        />
                                    </td>
                                    <td className="px-4 py-2.5">
                                        <div className="flex items-center gap-2">
                                            <span className="font-semibold text-gray-800 dark:text-gray-100 truncate max-w-[240px]">
                                                {l.cliente_nome}
                                            </span>
                                            {!l.adimplente && (
                                                <span
                                                    className="px-1.5 py-0.5 text-[9px] font-bold rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                                                    title="Tem boleto Vigor vencido — a visita ainda pode ser atribuída, mas o gestor decide."
                                                >
                                                    Inadimplente
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-4 py-2.5 text-gray-600 dark:text-gray-300">
                                        <span className="flex items-center gap-1">
                                            {l.cidade ? <MapPin size={12} className="text-gray-400" /> : null}
                                            {l.cidade || '—'}
                                            {geoAproximado(l) ? (
                                                <AlertTriangle
                                                    size={11}
                                                    className="text-amber-500 shrink-0"
                                                    aria-label={avisoPrecisao(l)}
                                                >
                                                    <title>{avisoPrecisao(l)}</title>
                                                </AlertTriangle>
                                            ) : null}
                                            {ancora != null && !ehAncora && (
                                                <span className="text-[10px] font-bold text-secondary shrink-0">
                                                    {km == null ? 'sem local'
                                                        : km < 1 ? `${Math.round(km * 1000)} m`
                                                            : `${km.toFixed(1)} km`}
                                                </span>
                                            )}
                                        </span>
                                    </td>
                                    <td className="px-4 py-2.5 text-gray-600 dark:text-gray-300 truncate max-w-[180px]">
                                        {nomeDe(profiles, l.responsavel_id)}
                                    </td>
                                    <td className="px-4 py-2.5 text-right">
                                        <span className={`px-2 py-0.5 text-xs font-bold rounded-full ${corDias(l.dias_sem_contato)}`}>
                                            {l.dias_sem_contato}
                                        </span>
                                    </td>
                                    <td className="px-4 py-2.5 text-right text-gray-600 dark:text-gray-300">
                                        <span className="inline-flex items-center gap-1">
                                            <Zap size={11} className="text-secondary" />
                                            {Number(l.consumo_medio_kwh || 0).toLocaleString('pt-BR', { maximumFractionDigits: 0 })}
                                        </span>
                                    </td>
                                    <td className="px-4 py-2.5 text-right" onClick={e => e.stopPropagation()}>
                                        <button
                                            onClick={() => setAtribuir([l])}
                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-full bg-primary text-white hover:opacity-90 transition-opacity"
                                        >
                                            <CalendarPlus size={13} />
                                            Atribuir
                                        </button>
                                    </td>
                                </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            <p className="flex items-center gap-1.5 text-[11px] text-gray-400">
                <UserPlus size={12} />
                Para um cliente fora da lista, abra a ficha dele no CRM e crie a visita por lá — ela entra como atribuição manual.
            </p>

            {atribuir && atribuir.length > 0 && (
                <CriarVisitaModal
                    negociacao={atribuir.map(paraLead)}
                    tipo="pos_venda"
                    origemAtribuicao="sugestao_sistema"
                    responsaveis={opcoesResponsavel}
                    onClose={() => setAtribuir(null)}
                    onCreated={() => {
                        // Os atribuídos somem da lista (ROT-R14) — a seleção deles não faz mais sentido.
                        const ids = atribuir.map(l => l.negociacao_id);
                        setSelecionados(prev => prev.filter(id => !ids.includes(id)));
                        setAtribuir(null);
                        carregar();
                    }}
                />
            )}
        </div>
    );
};

export default PosVendaView;
